import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Home, Users, GraduationCap, HeartHandshake, User } from 'lucide-react';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import './WhatMovedNotice.css';

/**
 * WhatMovedNotice — one-time "here's what moved" sheet.
 *
 * The navigation restructure ships to everyone at once with no feature flag,
 * and a large share of our users are elderly or came across from PrayDay and
 * know the old paths by muscle memory. This is the only thing that tells them
 * where their features went, so it is deliberately a plain list they can read
 * once and dismiss — not a multi-step coach-mark tour.
 *
 * Persistence follows PageGuide: a single localStorage flag, written on
 * dismissal. The `-v2` suffix is the nav version, so a future restructure can
 * show its own notice without clearing this one.
 */

const STORAGE_KEY = 'ignite-nav-moved-v2';

// Accounts created on or after this date never saw the old navigation, so the
// notice would be describing a change they did not experience. Set to the day
// the restructure ships; harmless to leave in place afterwards.
const NAV_V2_SHIPPED_AT = Date.parse('2026-08-03T00:00:00Z');

export function hasSeenWhatMoved() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return true; }
}

export default function WhatMovedNotice() {
  const { t } = useTranslation('nav');
  const { dbUser } = useAuth();
  // Read once at mount. Reading on every render would reopen the sheet if
  // another tab cleared the flag mid-session.
  const [dismissed, setDismissed] = useState(hasSeenWhatMoved);

  // Wait for the profile before deciding — rendering then retracting the sheet
  // would be worse than a beat of delay.
  if (!dbUser) return null;
  const createdAt = Date.parse(dbUser.createdAt);
  if (Number.isFinite(createdAt) && createdAt >= NAV_V2_SHIPPED_AT) return null;

  if (dismissed) return null;

  function handleGotIt() {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setDismissed(true);
  }

  const rows = [
    { Icon: Home,           key: 'home'    },
    { Icon: Users,          key: 'people'  },
    { Icon: GraduationCap,  key: 'grow'    },
    { Icon: HeartHandshake, key: 'prayer'  },
    { Icon: User,           key: 'account' },
  ];

  return ReactDOM.createPortal(
    <div className="wm-overlay" role="dialog" aria-modal="true" aria-labelledby="wm-title">
      <div className="wm-sheet">
        <h2 className="wm-title" id="wm-title">{t('whatMoved.title')}</h2>
        <p className="wm-intro">{t('whatMoved.intro')}</p>

        <ul className="wm-list">
          {rows.map(({ Icon, key }) => (
            <li className="wm-row" key={key}>
              <span className="wm-row-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.9} />
              </span>
              {/* The strings bold the destination name; they are our own
                  locale files, never user input. */}
              <span
                className="wm-row-text"
                dangerouslySetInnerHTML={{ __html: t(`whatMoved.${key}`) }} />
            </li>
          ))}
        </ul>

        <button className="btn btn-primary wm-got-it" onClick={handleGotIt} autoFocus>
          {t('whatMoved.gotIt')}
        </button>
      </div>
    </div>,
    document.body
  );
}
